import { DestroyRef, Injectable, Signal, computed, effect, inject, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { DebugLayoutToggleService } from './debug-layout-toggle.service';

/**
 * Non-production helper that compresses a wide XBRL grid down to a small,
 * scrollable window of columns. Bound to three chords (all `Ctrl+Alt+…` to
 * dodge browser/OS defaults):
 *
 *   - `Ctrl+Alt+T` — toggle compression on/off.
 *   - `Ctrl+Alt+1` — show the PREVIOUS page of columns.
 *   - `Ctrl+Alt+2` — show the NEXT page of columns.
 *
 * Why: Statement of Changes in Equity (role19) renders up to ~26 equity
 * columns per table. When chasing a validation error in the "Error
 * Description" column the amount columns are mostly noise. With compression
 * ON the consuming sheet renders only `pageSize` (3) amount columns at a
 * time and reclaims the freed width for the Error Description column, so the
 * error prose wraps onto far fewer lines. The fixed columns the sheet always
 * needs — the field-row name, Actions, Mapping Status, Error Description —
 * are never paged; only the amount columns are.
 *
 * ─── Page window ──────────────────────────────────────────────────────
 *
 * The service owns the *navigation* state (which page is showing) but not
 * the columns themselves — it doesn't know what a column is. The consuming
 * sheet tells it how many amount columns exist via `setColumnCount(n)`, and
 * reads back `windowStart()` to slice its own column array
 * `[windowStart, windowStart + pageSize)`. The last page naturally shows the
 * remainder (1 or 2 columns) because the slice is bounded by the array end.
 *
 * `pageIndex` is clamped to `[0, pageCount - 1]` on every navigation and
 * whenever `setColumnCount` shrinks the column set, so the consumer can never
 * read an out-of-range window. Toggling compression OFF resets to page 0 so
 * a later re-enable starts from the first window.
 *
 * ─── Scope guards (why nothing happens in prod or under Ctrl+Alt+D) ─────
 *
 *   - **Production:** `bindGlobalShortcut` short-circuits on
 *     `environment.production` (a compile-time const, so the listener body is
 *     dead-code-eliminated by the optimiser). The signal stays `false` and
 *     consumers additionally guard their template branch with
 *     `EnvironmentService.isProduction()`.
 *   - **Debug layout (Ctrl+Alt+D):** the chords are ignored while
 *     `DebugLayoutToggleService.effectiveEnabled()` is true. Column
 *     compression only makes sense on the ORIGINAL 4-table grid; the
 *     refactored card layout (Ctrl+Alt+D) is not a grid, so the chords are a
 *     no-op there. This mirrors the same suppression added to
 *     `DebugErrorCompressionToggleService` (Ctrl+Alt+E) — both render-tweak
 *     toggles are operational only on the original grid.
 *
 * Like `DebugErrorCompressionToggleService`, this is a pure render concern —
 * no FormControls are created/destroyed — so there is no transition spinner
 * machinery (cf. `DebugLayoutToggleService`).
 */
@Injectable({ providedIn: 'root' })
export class DebugColumnPagingToggleService {
  /** Amount columns shown per page when compression is ON. */
  readonly pageSize = 3;

  private readonly _enabled = signal<boolean>(false);
  private readonly _pageIndex = signal<number>(0);
  private readonly _columnCount = signal<number>(0);

  /** Compression on/off — what templates branch their layout on. */
  readonly enabled: Signal<boolean> = this._enabled.asReadonly();
  /** Zero-based index of the currently shown page of columns. */
  readonly pageIndex: Signal<number> = this._pageIndex.asReadonly();

  /** Total number of pages given the current column count (0 when no columns). */
  readonly pageCount = computed<number>(() => {
    const cols = this._columnCount();
    return cols <= 0 ? 0 : Math.ceil(cols / this.pageSize);
  });

  /** First column index of the visible window — consumers slice from here. */
  readonly windowStart = computed<number>(() => this._pageIndex() * this.pageSize);

  private readonly debugLayout = inject(DebugLayoutToggleService);
  private readonly handler = (event: KeyboardEvent) => this.onKey(event);
  private refCount = 0;

  constructor() {
    // Entering the Ctrl+Alt+D debug layout forces compression OFF and rewinds
    // to the first page, so returning to the original 4-table grid shows the
    // full, un-paged set of columns by default. Reads the intended `enabled`
    // signal (flips synchronously on the keypress), so the reset lands the
    // moment the user switches into debug mode.
    effect(() => {
      if (this.debugLayout.enabled()) {
        this._enabled.set(false);
        this._pageIndex.set(0);
      }
    });
  }

  bindGlobalShortcut(destroyRef: DestroyRef): void {
    if (environment.production) return;

    if (this.refCount === 0) {
      window.addEventListener('keydown', this.handler);
    }
    this.refCount++;

    destroyRef.onDestroy(() => {
      this.refCount = Math.max(0, this.refCount - 1);
      if (this.refCount === 0) {
        window.removeEventListener('keydown', this.handler);
      }
    });
  }

  /**
   * Tell the service how many pageable columns the consumer has. Re-clamps
   * the current page so a shrunk column set can't leave the window dangling
   * past the end. Safe to call on every schema (re)load.
   */
  setColumnCount(count: number): void {
    this._columnCount.set(Math.max(0, Math.floor(count)));
    this.clampPageIndex();
  }

  private onKey(event: KeyboardEvent): void {
    if (!event.ctrlKey || !event.altKey || event.shiftKey || event.metaKey) return;
    // Only operate on the original 4-table grid — never under Ctrl+Alt+D.
    if (this.debugLayout.effectiveEnabled()) return;

    switch (event.code) {
      case 'KeyT':
        event.preventDefault();
        this.toggle();
        return;
      case 'Digit1':
        // Paging only makes sense while compression is on.
        if (!this._enabled()) return;
        event.preventDefault();
        this.previous();
        return;
      case 'Digit2':
        if (!this._enabled()) return;
        event.preventDefault();
        this.next();
        return;
    }
  }

  private toggle(): void {
    const next = !this._enabled();
    this._enabled.set(next);
    if (!next) this._pageIndex.set(0); // reset window when turning OFF
  }

  private previous(): void {
    this._pageIndex.update((pageIndex) => Math.max(0, pageIndex - 1));
  }

  private next(): void {
    const maxIndex = Math.max(0, this.pageCount() - 1);
    this._pageIndex.update((pageIndex) => Math.min(maxIndex, pageIndex + 1));
  }

  private clampPageIndex(): void {
    const maxIndex = Math.max(0, this.pageCount() - 1);
    if (this._pageIndex() > maxIndex) this._pageIndex.set(maxIndex);
  }
}
